import { type RefObject, useCallback, useRef, useState } from 'react';

import { generateBenefitsPdfBlob } from '@/modules/autoVerify/benefitsPage/helpers/benefitsPdfGenerator';

export interface UseCoverageReportPdfReturn {
  contentRef: RefObject<HTMLDivElement>;
  isGenerating: boolean;
  downloadPdf: (fileName: string) => Promise<void>;
}

/**
 * Hook for DOM-based PDF generation of the coverage report.
 * Captures the contentRef element via html2canvas + jsPDF and triggers a download.
 */
export const useCoverageReportPdf = (): UseCoverageReportPdfReturn => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const downloadPdf = useCallback(async (fileName: string): Promise<void> => {
    const element = contentRef.current;
    if (!element) {
      throw new Error('Coverage report content ref not set');
    }
    setIsGenerating(true);
    try {
      const blob = await generateBenefitsPdfBlob(element);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setIsGenerating(false);
    }
  }, []);

  return { contentRef, isGenerating, downloadPdf };
};
